import MathInput from "components/inputs/mathInput"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { ProductActionType, ProductActionWrite } from "types"
import useActions from "hooks/useActions"
import Callout from "components/callout/callout"
import FormErrors from "components/formParts/formErrors"
import { decodeFirebaseError } from "utils/firebaseHelpers"
import StockFormCallout from "components/callout/stockFormCallout"
import TextAreaInput from "components/inputs/textareaInput"
import useProducts from "hooks/useProducts"
import useLayout from "hooks/useLayout"
import Form from "components/forms/form"
import { ReactComponent as EqualIcon } from "assets/icons/equal.svg"
import Button from "components/inputs/button"
import ButtonGroup from "components/buttons/buttonGroup"
import Section from "components/layout/section"
import ProcessingButton from "components/inputs/processingButton"
import ProductSelector from "@/components/searchAndSelect/ProductSelector"
import useStorages from "hooks/useStorages"

interface AdjustProductStockFormProps {
    storageId: string
    productIdPreset?: string
}

const AdjustProductStockForm: React.FC<AdjustProductStockFormProps> = ({ storageId, productIdPreset }) => {

    const { t } = useTranslation()
    

    const [amount, setAmount] = useState(`0`)
    const [description, setDescription] = useState(``)
    const [productId, setProductId] = useState<string>('')
    const [errors, setErrors] = useState<string[]>([])

    const { addToast, closeDialog } = useLayout()

    const {
    productsStatus,
    products
    } = useProducts({ storageId, productId })

    const { adjustInventoryAsync, isAdjusting } = useActions({ storageId })
    const { isStockAdjustmentEnabled } = useStorages({ storageId })
    const stockAdjustmentEnabled = isStockAdjustmentEnabled()

    // Grab provided productId.
    useEffect(() => {
        if (!productIdPreset) return
        setProductId(productIdPreset)
    }, [productIdPreset])

    const product = useMemo(() => products.find(i => i.id === productId), [products, productId])

    const isValid = useMemo(() => {
        if (product?.status?.currentAmount === undefined) return
        const num = Number(amount)
        if ( num > 0 ) {
            if ( product.stockSettings.allowNegativeInventory && product.status.currentAmount - num < 0 ) {
                return true
            } else if ( product.status.currentAmount - num >= 0 ) {
                return true
            } else {
                return false
            }
        } else {
            return false
        }
    }, [amount, product])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setErrors([])
        if ( amount === null || !isValid ) return
        if (!stockAdjustmentEnabled) {
            setErrors(['ACTION_ADJUST_NOT_ALLOWED'])
            return
        }
        
        try {
            const actionData: ProductActionWrite = {
                type: ProductActionType.ADJUST,
                ids: {
                    storageId,
                    productId
                },
                amount: Number(amount),
                description: description
                // Note: Adjust actions cannot be linked to transactions
            }
            const result = await adjustInventoryAsync(actionData)
            addToast({ text: t('toastProductStockAdjusted'), colorScheme: 'green' })
            closeDialog()
        } catch (error: unknown) {
            const errorMessage = decodeFirebaseError(error instanceof Error ? error.message : String(error))
            setErrors([errorMessage])
        }
    }

    // Handle change description.
    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setDescription(e.target.value)
    }

    if (productsStatus === `loading` && !product) return <Callout type={`neutral`} loadingIcon={true}>{t('loadingProducts')}</Callout>

    return (
        <Form
            onSubmit={handleSubmit}
            className={`group`}
            isLoading={isAdjusting}
        >
            <Section>
            <Callout type="info" id="calloutAdjustProductsInInventory"><p>{t('calloutAdjustProductsInInventory')}</p></Callout>
            {stockAdjustmentEnabled === false && (
                <Callout type="error">
                    <p>{t('calloutStockAdjustmentDisabled')}</p>
                </Callout>
            )}
            <div className={`input-holder-wide`}>
                <label>{t('selectProduct')}*</label>
                <ProductSelector 
                    storageId={storageId}
                    selectedProductCode={productId}
                    onProductSelect={setProductId}
                    placeholder={t('filterForIdOrName')}
                    inlineResults={true}
                />
            </div>
            {productId && product && product.status?.currentAmount !== undefined && (
                <>
                    {product.stockSettings.allowNegativeInventory && <Callout type={`info`}>{t('negativeNumbersDuringProductAdjustment')}</Callout>}
                    <MathInput value={amount} setValue={setAmount} isRequired={true} negativeIsAllowed={product.stockSettings.allowNegativeInventory} label={t('amount')} />
                    <TextAreaInput label={t('description')} value={description} onChange={handleDescriptionChange} maxLength={200} />
                    {/* Note: Adjustment actions cannot be linked to transactions */}
                </>
            )}
            <StockFormCallout product={product} amount={Number(amount)} />
            <ButtonGroup>
                {isAdjusting ? (
                    <ProcessingButton design="primary" />
                ) : (
                    <Button wideOnMobile type="submit" design="primary" disabled={!isValid || !stockAdjustmentEnabled}><EqualIcon /><span>{t('buttonAddStockAdjustment')}</span></Button>
                )}
            </ButtonGroup>
            {errors.length > 0 && <FormErrors errors={errors} />}
            </Section>
        </Form>
    )

}

export default AdjustProductStockForm



